<template>
  <main class="acceso">
    <!-- Encabezado (mismo patrón que Dashboard) -->
    <header class="acceso-head">
      <div>
        <div class="sec-title">
          <span class="bar-accent"></span>
          Acceso
        </div>
        <div class="sec-sub">
          Asigna cuentas y áreas a los gerentes. Solo el administrador CX ve este módulo.
        </div>
      </div>
      <button class="acceso-reload" :disabled="cargando" @click="cargar">
        <i class="bi bi-arrow-clockwise"></i>
        {{ cargando ? "Cargando…" : "Actualizar" }}
      </button>
    </header>

    <div v-if="cargando" class="empty">Cargando módulo de Acceso…</div>

    <template v-else>
      <!-- Resumen por rol -->
      <div class="rol-stats">
        <div
          v-for="r in resumenRoles"
          :key="r.clave"
          class="stat"
          :class="{ active: filtroRol === r.clave }"
          role="button"
          tabindex="0"
          @click="toggleRol(r.clave)"
          @keydown.enter="toggleRol(r.clave)"
        >
          <div class="n" :style="{ color: rolColor(r.clave) }">{{ r.n }}</div>
          <div class="l">{{ rolLabel(r.clave) }}</div>
        </div>
      </div>

      <!-- Layout master-detail -->
      <div class="layout">
        <!-- Columna izquierda: buscador + tabla -->
        <section class="izq">
          <div class="toolbar">
            <div class="search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                v-model="q"
                placeholder="Buscar por nombre o email…"
                class="search-inp"
              />
            </div>
          </div>

          <div class="card tabla-wrap">
            <table class="tabla">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Rol</th>
                  <th class="num">Cuentas</th>
                  <th class="num">Áreas</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="c in filtrados"
                  :key="c.idColaborador"
                  :class="{ sel: sel && sel.idColaborador === c.idColaborador }"
                  @click="seleccionar(c)"
                >
                  <td>
                    <div class="c-nombre">{{ c.nombre || "—" }}</div>
                    <div class="c-email">{{ c.email || "—" }}</div>
                  </td>
                  <td>
                    <span class="rol-badge" :style="rolBadgeStyle(c.rol)">
                      {{ rolLabel(c.rol) }}
                    </span>
                  </td>
                  <td class="num">{{ cuentaCount(c.idColaborador) }}</td>
                  <td class="num">{{ areaCount(c.idColaborador) }}</td>
                </tr>
                <tr v-if="filtrados.length === 0">
                  <td colspan="4" class="sin-resultados">
                    Sin colaboradores que coincidan con la búsqueda.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Columna derecha: detalle del seleccionado -->
        <aside class="der">
          <div v-if="sel" class="card detalle">
            <div class="detalle-head">
              <div>
                <div class="detalle-nombre">{{ sel.nombre || "Sin nombre" }}</div>
                <div class="detalle-email">{{ sel.email || "—" }}</div>
              </div>
              <label class="rol-editor">
                <span class="label-custom">Rol</span>
                <select
                  :value="sel.rol || ''"
                  class="rol-sel"
                  @change="cambiarRol(sel, $event.target.value)"
                >
                  <option value="">(sin rol)</option>
                  <option v-for="r in ROLES" :key="r" :value="r">{{ rolLabel(r) }}</option>
                </select>
              </label>
            </div>

            <section class="col">
              <h4 class="col-title">Cuentas asignadas</h4>
              <ul class="asig-list">
                <li v-for="a in cuentasDe(sel.idColaborador)" :key="a.idAcceso">
                  <span>{{ a.cuenta }}</span>
                  <button class="x" title="Quitar cuenta" @click="quitar(a)">×</button>
                </li>
                <li v-if="cuentasDe(sel.idColaborador).length === 0" class="vacio">
                  Sin cuentas asignadas.
                </li>
              </ul>
              <div class="add">
                <select v-model="nuevaCuenta" class="inp">
                  <option :value="null">Selecciona una cuenta…</option>
                  <option v-for="cu in cuentas" :key="cu.idCuenta" :value="cu.idCuenta">
                    {{ cu.nombre }}
                  </option>
                </select>
                <button class="btn" :disabled="!nuevaCuenta" @click="asignar('CUENTA')">
                  Agregar
                </button>
              </div>
            </section>

            <section class="col">
              <h4 class="col-title">Áreas asignadas</h4>
              <ul class="asig-list">
                <li v-for="a in areasDe(sel.idColaborador)" :key="a.idAcceso">
                  <span>{{ a.area }}</span>
                  <button class="x" title="Quitar área" @click="quitar(a)">×</button>
                </li>
                <li v-if="areasDe(sel.idColaborador).length === 0" class="vacio">
                  Sin áreas asignadas.
                </li>
              </ul>
              <div class="add">
                <select v-model="nuevaArea" class="inp">
                  <option :value="null">Selecciona un área…</option>
                  <option v-for="ar in areas" :key="ar.idArea" :value="ar.idArea">
                    {{ ar.descripcion }}
                  </option>
                </select>
                <button class="btn" :disabled="!nuevaArea" @click="asignar('AREA')">
                  Agregar
                </button>
              </div>
            </section>
          </div>

          <div v-else class="card detalle vacio-detalle">
            <div class="vacio-ic"><i class="bi bi-person-gear"></i></div>
            <p>Selecciona un colaborador para ver y editar sus accesos.</p>
          </div>
        </aside>
      </div>
    </template>

    <div v-if="error" class="error">{{ error }}</div>
  </main>
</template>

<script>
import accesoService from "../services/acceso.service";
import { sesion } from "../services/cx.service";

const ROLES = ["admin", "direccion", "gerente", "gerente_area", "cx", "responsable"];

const ROL_META = {
  admin: { label: "Admin CX", color: "#003CA5" },
  direccion: { label: "Dirección Comercial", color: "#7c3aed" },
  gerente: { label: "Gerente de cuenta", color: "#43B234" },
  gerente_area: { label: "Gerente de área", color: "#d97706" },
  cx: { label: "CX", color: "#0891b2" },
  responsable: { label: "Responsable", color: "#64748b" },
};

export default {
  name: "AccesoAdmin",
  data() {
    return {
      ROLES,
      cargando: true,
      error: "",
      colaboradores: [],
      cuentas: [],
      areas: [],
      asignaciones: [],
      sel: null,
      nuevaCuenta: null,
      nuevaArea: null,
      q: "",
      filtroRol: null,
    };
  },
  computed: {
    resumenRoles() {
      const m = {};
      this.colaboradores.forEach((c) => {
        const k = c.rol || "";
        m[k] = (m[k] || 0) + 1;
      });
      return Object.keys(m)
        .sort()
        .map((k) => ({ clave: k, n: m[k] }));
    },
    filtrados() {
      const t = this.q.trim().toLowerCase();
      return this.colaboradores.filter((c) => {
        if (this.filtroRol !== null && (c.rol || "") !== this.filtroRol) return false;
        if (!t) return true;
        return (
          (c.nombre || "").toLowerCase().includes(t) ||
          (c.email || "").toLowerCase().includes(t)
        );
      });
    },
  },
  async created() {
    try {
      const s = await sesion().catch(() => ({ rol: "" }));
      if (s.rol !== "admin") {
        this.$router.replace("/");
        return;
      }
      await this.cargar();
    } catch (e) {
      this.error = e.message || "No se pudo cargar el módulo de Acceso.";
    } finally {
      this.cargando = false;
    }
  },
  methods: {
    rolLabel(rol) {
      return (ROL_META[rol] && ROL_META[rol].label) || "Sin rol";
    },
    rolColor(rol) {
      return (ROL_META[rol] && ROL_META[rol].color) || "#94a3b8";
    },
    rolBadgeStyle(rol) {
      const c = this.rolColor(rol);
      return { color: c, borderColor: c + "55", background: c + "14" };
    },
    toggleRol(clave) {
      this.filtroRol = this.filtroRol === clave ? null : clave;
    },
    async cargar() {
      this.cargando = true;
      this.error = "";
      try {
        const [cat, acc] = await Promise.all([
          accesoService.fetchCatalogos(),
          accesoService.fetchAcceso(),
        ]);
        this.colaboradores = (cat && cat.colaboradores) || [];
        this.cuentas = (cat && cat.cuentas) || [];
        this.areas = (cat && cat.areas) || [];
        this.asignaciones = acc || [];
      } catch (e) {
        this.error = e.message || "No se pudo cargar el módulo de Acceso.";
      } finally {
        this.cargando = false;
      }
    },
    seleccionar(c) {
      this.sel = c;
      this.nuevaCuenta = null;
      this.nuevaArea = null;
    },
    cuentaCount(id) {
      return this.asignaciones.filter(
        (a) => a.idColaborador === id && a.tipo === "CUENTA"
      ).length;
    },
    areaCount(id) {
      return this.asignaciones.filter(
        (a) => a.idColaborador === id && a.tipo === "AREA"
      ).length;
    },
    cuentasDe(id) {
      return this.asignaciones.filter(
        (a) => a.idColaborador === id && a.tipo === "CUENTA"
      );
    },
    areasDe(id) {
      return this.asignaciones.filter(
        (a) => a.idColaborador === id && a.tipo === "AREA"
      );
    },
    async asignar(tipo) {
      const idRef = tipo === "CUENTA" ? this.nuevaCuenta : this.nuevaArea;
      if (!this.sel || !idRef) return;
      try {
        await accesoService.asignar({
          idColaborador: this.sel.idColaborador,
          tipo,
          idCuenta: tipo === "CUENTA" ? idRef : null,
          idArea: tipo === "AREA" ? idRef : null,
        });
        this.nuevaCuenta = null;
        this.nuevaArea = null;
        await this.cargar();
        this.reseleccionar();
      } catch (e) {
        this.error = e.message;
      }
    },
    async quitar(a) {
      try {
        await accesoService.quitar(a.idAcceso);
        await this.cargar();
        this.reseleccionar();
      } catch (e) {
        this.error = e.message;
      }
    },
    async cambiarRol(c, rol) {
      try {
        await accesoService.setRol(c.idColaborador, rol);
        c.rol = rol;
      } catch (e) {
        this.error = e.message;
      }
    },
    reseleccionar() {
      if (this.sel) {
        this.sel =
          this.colaboradores.find((x) => x.idColaborador === this.sel.idColaborador) ||
          null;
      }
    },
  },
};
</script>

<style scoped>
.acceso {
  padding: 20px clamp(16px, 3vw, 32px) 40px;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Encabezado (mismo patrón que Dashboard) */
.acceso-head {
  display: flex;
  background: var(--white);
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.acceso-head .sec-title {
  margin: 0;
}
.acceso-head .sec-sub {
  margin: 6px 0 0 14px;
}
.acceso-reload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt-dim);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.acceso-reload:hover:not(:disabled) {
  border-color: var(--border-2);
  color: var(--txt);
  background: var(--surface);
}
.acceso-reload:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Stats por rol */
.rol-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.stat {
  cursor: pointer;
  transition: all 0.25s ease;
}
.stat.active {
  border-color: var(--secondary);
  box-shadow: 0 0 0 2px var(--secondary), var(--shadow-sm);
}

/* Master-detail */
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}
.izq {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.der {
  position: sticky;
  top: 16px;
  min-width: 0;
}

/* Búsqueda */
.search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--white);
  border: 1px solid var(--border);
}
.search svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: var(--txt-faint);
  stroke-width: 2;
  stroke-linecap: round;
  flex-shrink: 0;
}
.search-inp {
  flex: 1;
  background: transparent;
  border: 0;
  outline: none;
  color: var(--txt);
  font-size: 14px;
  font-family: inherit;
}
.search-inp::placeholder {
  color: var(--txt-faint);
}

/* Tarjetas */
.card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
}

/* Tabla */
.tabla-wrap {
  overflow: hidden;
}
.tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.tabla th {
  text-align: left;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
}
.tabla td {
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
.tabla tbody tr:last-child td {
  border-bottom: none;
}
.tabla tbody tr {
  cursor: pointer;
  transition: background 0.15s ease;
}
.tabla tbody tr:hover td {
  background: var(--surface);
}
.tabla tbody tr.sel td {
  background: var(--surface-2);
}
.c-nombre {
  font-weight: 600;
  color: var(--txt);
}
.c-email {
  font-size: 12px;
  color: var(--txt-faint);
  margin-top: 2px;
}
.num {
  font-family: var(--mono);
  font-weight: 600;
  text-align: right;
}
.sin-resultados {
  text-align: center;
  color: var(--txt-faint);
  padding: 24px;
}

/* Badge de rol */
.rol-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}

/* Detalle */
.detalle {
  padding: 18px;
}
.detalle-head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
}
.detalle-nombre {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--txt);
  word-break: break-word;
}
.detalle-email {
  font-size: 13px;
  color: var(--txt-faint);
}
.rol-editor {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rol-sel,
.inp {
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt);
  font-size: 14px;
  font-family: inherit;
}
.rol-sel:focus,
.inp:focus {
  outline: none;
  border-color: var(--primary);
}

.col {
  margin-bottom: 18px;
}
.col:last-child {
  margin-bottom: 0;
}
.col-title {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 700;
}
.asig-list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.asig-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--txt);
}
.asig-list li.vacio {
  color: var(--txt-faint);
  justify-content: flex-start;
  background: none;
  border-style: dashed;
}
.x {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--txt-faint);
  font-size: 18px;
  line-height: 1;
}
.x:hover {
  color: var(--bad);
}
.add {
  display: flex;
  gap: 8px;
}
.add .inp {
  flex: 1;
  min-width: 0;
}
.btn {
  padding: 8px 16px;
  border: 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(67, 178, 52, 0.25);
  transition: all 0.2s ease;
  white-space: nowrap;
}
.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}
.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Detalle vacío */
.vacio-detalle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  min-height: 200px;
  color: var(--txt-faint);
  border-style: dashed;
}
.vacio-ic {
  font-size: 32px;
  color: var(--txt-faint);
}
.vacio-detalle p {
  margin: 0;
  font-size: 14px;
}

.error {
  color: var(--bad);
  background: rgba(255, 93, 115, 0.08);
  border: 1px solid rgba(255, 93, 115, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
}

@media (max-width: 900px) {
  .layout {
    grid-template-columns: 1fr;
  }
  .der {
    position: static;
  }
}
</style>
